/**
 * VelocityTrendArrow
 *
 * Tiny arrow showing post velocity trend:
 * - Up primary green when trend > 10%
 * - Down coral when trend < -10%
 * - Neutral gray otherwise
 *
 * Reads either week-over-week (default) or month-over-month, controlled by the
 * page-level velocity-period toggle. Wrapped in GlossaryTooltip for the
 * "metric-velocity" definition.
 */

import { useTranslation } from 'react-i18next';
import { GlossaryTooltip } from './GlossaryTooltip';
import type { VelocityData } from '@/data/portfolio.types';

interface VelocityTrendArrowProps {
  velocity: VelocityData;
  /** 'week' (default) compares this week vs prior week.
   *  'month' compares rolling 30 days vs the prior 30. */
  period?: 'week' | 'month';
  /** On mobile, do not wrap in the tap-to-open glossary sheet (use inside tappable cards). */
  disableGlossaryOnMobile?: boolean;
}

export const VelocityTrendArrow = ({ velocity, period = 'week', disableGlossaryOnMobile = false }: VelocityTrendArrowProps) => {
  const { t } = useTranslation('agency');
  const useMonth = period === 'month';
  const current = useMonth ? velocity.postsThisMonth ?? 0 : velocity.postsThisWeek;
  const prior = useMonth ? velocity.postsPriorMonth ?? 0 : velocity.postsPriorWeek;
  const trend = useMonth ? velocity.trendMonthPct ?? 0 : velocity.trendPct;
  const ariaDetail = useMonth
    ? t('portfolio_velocity_detail_month', { current, prior })
    : t('portfolio_velocity_detail_week', { current, prior });

  if (trend > 10) {
    return (
      <GlossaryTooltip termId="metric-velocity" side="top" disableOnMobile={disableGlossaryOnMobile}>
        <span
          className="inline-flex items-center text-primary-500 shrink-0 cursor-default"
          aria-label={t('portfolio_velocity_up_aria', { pct: trend, detail: ariaDetail })}
          role="img"
        >
          <i className="hm hm-arrow-up" style={{ fontSize: 12 }} aria-hidden="true" />
        </span>
      </GlossaryTooltip>
    );
  }

  if (trend < -10) {
    return (
      <GlossaryTooltip termId="metric-velocity" side="top" disableOnMobile={disableGlossaryOnMobile}>
        <span
          className="inline-flex items-center text-coral-500 shrink-0 cursor-default"
          aria-label={t('portfolio_velocity_down_aria', { pct: Math.abs(trend), detail: ariaDetail })}
          role="img"
        >
          <i className="hm hm-arrow-down" style={{ fontSize: 12 }} aria-hidden="true" />
        </span>
      </GlossaryTooltip>
    );
  }

  return (
    <GlossaryTooltip termId="metric-velocity" side="top" disableOnMobile={disableGlossaryOnMobile}>
      <span
        className="inline-flex items-center text-gray-400 shrink-0 cursor-default"
        aria-label={t('portfolio_velocity_stable_aria', { detail: ariaDetail })}
        role="img"
      >
        <i className="hm hm-arrow-right" style={{ fontSize: 12 }} aria-hidden="true" />
      </span>
    </GlossaryTooltip>
  );
};
